<template>
    <v-dialog v-model="dialog" max-width="900" persistent @click:outside="handleClose">
        <v-card>
            <!-- Header -->
            <v-toolbar color="primary" elevation="0" class="white--text">
                <v-toolbar-title class="font-weight-bold white--text">
                    {{ $t('finance.loanPrograms.addDataField') }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon color="white" @click="handleClose">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text>
                <v-form ref="form" v-model="formValid">
                    <!-- Mandatory Toggle -->
                    <v-row >
                        <v-col cols="3" class="d-flex align-center">
                            <label class="font-weight-medium mr-4">{{ $t('finance.loanPrograms.mandatory') }}</label>
                            <span class="mr-3">{{ fieldData.isMandetory ? $t('finance.loanPrograms.yes') : $t('finance.loanPrograms.no') }}</span>
                            <v-switch
                                v-model="fieldData.isMandetory"
                                inset
                                dense
                                color="primary"
                            ></v-switch>
                        </v-col>
                        <v-col cols="3" class="d-flex align-center">
                            <label class="font-weight-medium mr-4">{{ $t('finance.loanPrograms.group') }}</label>
                            <v-icon small color="grey" class="mr-2">mdi-information-outline</v-icon>
                            <span class="mr-3">{{ fieldData.isGroup ? $t('finance.loanPrograms.yes') : $t('finance.loanPrograms.no') }}</span>
                            <v-switch
                                v-model="fieldData.isGroup"
                                inset
                                dense
                                color="primary"
                                @change="handleGroupToggle"
                            ></v-switch>
                        </v-col>
                    </v-row>

                    <v-row v-if="!fieldData.isGroup" >
                        <v-col cols="6">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.type') }} <span class="red--text">*</span>
                            </label>
                            <v-select
                                v-model="fieldData.dataType"
                                :items="dataTypeOptions"
                                item-text="label"
                                item-value="value"
                                outlined
                                dense
                                :placeholder="$t('finance.loanPrograms.selectDataType')"
                                :rules="[v => !!v || $t('finance.loanPrograms.fieldRequired')]"
                                hide-details
                                @change="handleDataTypeChange"
                            ></v-select>
                        </v-col>
                        <v-col cols="6">
                        <label class="font-weight-medium mb-2 d-block">
                            {{ $t('finance.loanPrograms.fieldTitle') }}
                        </label>
                        <v-text-field
                            v-model="fieldData.fieldTitle"
                            outlined
                            dense
                            :placeholder="$t('finance.loanPrograms.enterHere')"
                        ></v-text-field>
                    </v-col>
                    </v-row>

                    <!-- Group Title (only when Group is enabled) -->
                    <v-row v-if="fieldData.isGroup" >
                        <v-col cols="6">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.groupTitle') }}
                            </label>
                            <v-text-field
                                v-model="fieldData.groupTitle"
                                outlined
                                dense
                                :placeholder="$t('finance.loanPrograms.enterGroupTitle')"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <!-- Allow Multiple Inputs (for Text Field and Dropdown) -->
                    <v-row class="bg-grey-lighten-2" v-if="!fieldData.isGroup && (fieldData.dataType === 'text_field' || fieldData.dataType === 'dropdown')" >
                        <v-col cols="6">
                            <v-checkbox
                                v-model="fieldData.allowMultipleInputs"
                                :label="$t('finance.loanPrograms.allowMultipleInputs')"
                                dense
                            ></v-checkbox>
                        </v-col>
                    </v-row>

                    <!-- Allowed File Types (for Attach File) -->
                    <div v-if="!fieldData.isGroup && fieldData.dataType === 'attach_file'">
                        <div class="d-flex flex-column">
                            <label class="font-weight-medium mb-1 d-block">
                                {{ $t('finance.loanPrograms.allowedFileTypes') }}
                            </label>

                            <div class="d-flex justify-start gap-3  align-center flex-row">
                                    <v-checkbox class="mr-4" v-model="fieldData.allowedFileTypes" value="image"
                                        :label="$t('finance.loanPrograms.imageJpegGifPng')" hide-details />

                                    <v-checkbox class="mr-4" v-model="fieldData.allowedFileTypes" value="video"
                                        :label="$t('finance.loanPrograms.videoMp4')" hide-details />

                                    <v-checkbox class="mr-4" v-model="fieldData.allowedFileTypes" value="docx" :label="$t('finance.loanPrograms.docx')"
                                        hide-details />
                            </div>
                        </div>
                    </div>

                    <!-- Digital Signature -->
                    <div v-if="!fieldData.isGroup && fieldData.dataType === 'digital_signature'">
                        <div class="d-flex flex-column">
                            <label class="font-weight-medium mb-1 d-block">
                                {{ $t('finance.loanPrograms.allowedFileTypes') }}
                            </label>

                            <div class="d-flex justify-start gap-3  align-center flex-row">
                                    <v-checkbox class="mr-4" v-model="fieldData.digitalSignature" value="digital-signature"
                                        :label="$t('finance.loanPrograms.digitalSignatureLabel')" hide-details />

                                    <v-checkbox class="mr-4" v-model="fieldData.attachFile" value="attach-file"
                                        :label="$t('finance.loanPrograms.attachFile')" hide-details />

                            </div>
                        </div>
                    </div>


                    <!-- Options for Dropdown, Radio Button, and Check Boxes -->
                    <v-row class="bg-grey-lighten-2" v-if="!fieldData.isGroup && (fieldData.dataType === 'dropdown' || fieldData.dataType === 'radio_button' || fieldData.dataType === 'check_boxes')" >
                        <v-col cols="6">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.options') }}
                            </label>
                            <div class="d-flex mb-2">
                                <v-text-field
                                    v-model="newOption"
                                    outlined
                                    dense
                                    :placeholder="$t('finance.loanPrograms.typeHere')"
                                    class="mr-2"
                                    @keyup.enter="addOption"
                                ></v-text-field>
                                <v-btn color="primary" @click="addOption">
                                    {{ $t('Add') }}
                                </v-btn>
                            </div>
                            <div v-if="fieldData.options && fieldData.options.length > 0">
                                <div
                                    v-for="(option, index) in fieldData.options"
                                    :key="index"
                                    class="d-flex align-center mb-2"
                                >
                                    <v-icon small color="primary" class="mr-2">mdi-circle</v-icon>
                                    <span class="flex-grow-1">{{ option }}</span>
                                    <v-btn icon small color="error" @click="removeOption(index)">
                                        <v-icon small>mdi-close</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- Measurement Unit Fields -->
                    <template class="bg-grey-lighten-2" v-if="!fieldData.isGroup && fieldData.dataType === 'measurement_unit'">
                        <v-row >
                            <v-col cols="6">
                                <label class="font-weight-medium mb-2 d-block">
                                    {{ $t('finance.loanPrograms.unitType') }}
                                </label>
                                <v-text-field
                                    v-model="fieldData.unitType"
                                    outlined
                                    dense
                                    :placeholder="$t('finance.loanPrograms.selectUnitType')"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="6">
                                <label class="font-weight-medium mb-2 d-block">
                                    {{ $t('finance.loanPrograms.type') }}
                                </label>
                                <v-select
                                    v-model="fieldData.measurementType"
                                    :items="measurementTypeOptions"
                                    item-text="label"
                                    item-value="value"
                                    outlined
                                    dense
                                    :placeholder="$t('finance.loanPrograms.selectType')"
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row >
                            <v-col cols="6">
                                <v-checkbox
                                    v-model="fieldData.allowUserToChangeUnitType"
                                    :label="$t('finance.loanPrograms.allowUserToChangeUnitType')"
                                    dense
                                ></v-checkbox>
                            </v-col>
                        </v-row>
                    </template>

                    <!-- Group Fields (when Group is enabled) -->
                    <template v-if="fieldData.isGroup">
                        <div
                            v-for="(groupField, index) in fieldData.groupFields"
                            :key="index"
                            class="pa-4 mb-4 rounded-lg"
                            style="background-color: #f5f5f5;"
                        >   
                            <v-row >
                                <v-col cols="6" class="d-flex align-center">
                                    <label class="font-weight-medium mr-4">{{ $t('finance.loanPrograms.mandatory') }}</label>
                                    <span class="mr-3">{{ groupField.isMandetory ? $t('finance.loanPrograms.yes') : $t('finance.loanPrograms.no') }}</span>
                                    <v-switch
                                        v-model="groupField.isMandetory"
                                        inset
                                        dense
                                        color="primary"
                                    ></v-switch>
                                </v-col>
                            </v-row>

                            <v-row >
                                <v-col cols="6">
                                    <label class="font-weight-medium mb-2 d-block">
                                        {{ $t('finance.loanPrograms.type') }}
                                    </label>
                                    <v-select
                                        v-model="groupField.dataType"
                                        :items="dataTypeOptions"
                                        item-text="label"
                                        item-value="value"
                                        outlined
                                        dense
                                        @change="handleGroupFieldDataTypeChange(index)"
                                    ></v-select>
                                </v-col>
                                <v-col cols="6">
                                    <label class="font-weight-medium mb-2 d-block">
                                        {{ $t('finance.loanPrograms.fieldTitle') }}
                                    </label>
                                    <v-text-field
                                        v-model="groupField.fieldTitle"
                                        outlined
                                        dense
                                        :placeholder="$t('finance.loanPrograms.enterHere')"
                                    ></v-text-field>
                                </v-col>
                            </v-row>

                            <!-- Allow Multiple Inputs (group subfield) -->
                            <v-row v-if="groupField.dataType === 'text_field' || groupField.dataType === 'dropdown'">
                                <v-col cols="6">
                                    <v-checkbox
                                        v-model="groupField.allowMultipleInputs"
                                        :label="$t('finance.loanPrograms.allowMultipleInputs')"
                                        dense
                                    ></v-checkbox>
                                </v-col>
                            </v-row>

                            <!-- Allowed File Types (group subfield — Attach File) -->
                            <div v-if="groupField.dataType === 'attach_file'" class="mb-2">
                                <div class="d-flex flex-column">
                                    <label class="font-weight-medium mb-1 d-block">
                                        {{ $t('finance.loanPrograms.allowedFileTypes') }}
                                    </label>
                                    <div class="d-flex justify-start gap-3 align-center flex-row flex-wrap">
                                        <v-checkbox class="mr-4" v-model="groupField.allowedFileTypes" value="image"
                                            :label="$t('finance.loanPrograms.imageJpegGifPng')" hide-details />
                                        <v-checkbox class="mr-4" v-model="groupField.allowedFileTypes" value="video"
                                            :label="$t('finance.loanPrograms.videoMp4')" hide-details />
                                        <v-checkbox class="mr-4" v-model="groupField.allowedFileTypes" value="docx"
                                            :label="$t('finance.loanPrograms.docx')" hide-details />
                                    </div>
                                </div>
                            </div>

                            <!-- Digital Signature (group subfield) -->
                            <div v-if="groupField.dataType === 'digital_signature'" class="mb-2">
                                <div class="d-flex flex-column">
                                    <label class="font-weight-medium mb-1 d-block">
                                        {{ $t('finance.loanPrograms.allowedFileTypes') }}
                                    </label>
                                    <div class="d-flex justify-start gap-3 align-center flex-row flex-wrap">
                                        <v-checkbox class="mr-4" v-model="groupField.digitalSignature" value="digital-signature"
                                            :label="$t('finance.loanPrograms.digitalSignatureLabel')" hide-details />
                                        <v-checkbox class="mr-4" v-model="groupField.attachFile" value="attach-file"
                                            :label="$t('finance.loanPrograms.attachFile')" hide-details />
                                    </div>
                                </div>
                            </div>

                            <!-- Options for Dropdown, Radio Button, and Check Boxes in Group Fields -->
                            <v-row v-if="groupField.dataType === 'dropdown' || groupField.dataType === 'radio_button' || groupField.dataType === 'check_boxes'">
                                <v-col cols="6">
                                    <label class="font-weight-medium mb-2 d-block">
                                        {{ $t('finance.loanPrograms.options') }}
                                    </label>
                                    <div class="d-flex mb-2">
                                        <v-text-field
                                            v-model="groupField._newOption"
                                            outlined
                                            dense
                                            :placeholder="$t('finance.loanPrograms.typeHere')"
                                            class="mr-2"
                                            @keyup.enter="addGroupFieldOption(index)"
                                        ></v-text-field>
                                        <v-btn color="primary" @click="addGroupFieldOption(index)">
                                            {{ $t('Add') }}
                                        </v-btn>
                                    </div>
                                    <div v-if="groupField.options && groupField.options.length > 0">
                                        <div
                                            v-for="(option, optIndex) in groupField.options"
                                            :key="optIndex"
                                            class="d-flex align-center mb-2"
                                        >
                                            <v-icon small color="primary" class="mr-2">mdi-circle</v-icon>
                                            <span class="flex-grow-1">{{ option }}</span>
                                            <v-btn icon small color="error" @click="removeGroupFieldOption(index, optIndex)">
                                                <v-icon small>mdi-close</v-icon>
                                            </v-btn>
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>

                            <!-- Measurement Unit (group subfield) -->
                            <template v-if="groupField.dataType === 'measurement_unit'">
                                <v-row>
                                    <v-col cols="6">
                                        <label class="font-weight-medium mb-2 d-block">
                                            {{ $t('finance.loanPrograms.unitType') }}
                                        </label>
                                        <v-text-field
                                            v-model="groupField.unitType"
                                            outlined
                                            dense
                                            :placeholder="$t('finance.loanPrograms.selectUnitType')"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="6">
                                        <label class="font-weight-medium mb-2 d-block">
                                            {{ $t('finance.loanPrograms.type') }}
                                        </label>
                                        <v-select
                                            v-model="groupField.measurementType"
                                            :items="measurementTypeOptions"
                                            item-text="label"
                                            item-value="value"
                                            outlined
                                            dense
                                            :placeholder="$t('finance.loanPrograms.selectType')"
                                        ></v-select>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="6">
                                        <v-checkbox
                                            v-model="groupField.allowUserToChangeUnitType"
                                            :label="$t('finance.loanPrograms.allowUserToChangeUnitType')"
                                            dense
                                        ></v-checkbox>
                                    </v-col>
                                </v-row>
                            </template>

                            <v-row >
                                <v-col cols="6" class="d-flex align-center">
                                    <label class="font-weight-medium mr-2">{{ $t('finance.loanPrograms.help') }}</label>
                                    <v-tooltip top color="00BD73" max-width="350">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon size="medium" class="icon-with-background mr-2"
                                                style="color:#0EBF67" v-bind="attrs" v-on="on">
                                                mdi-alert-circle
                                            </v-icon>
                                        </template>
                                        <span>{{ $t('finance.loanPrograms.writeHelpTextUsers') }}</span>
                                    </v-tooltip>
                                    <span class="mr-3">{{ groupField.help ? $t('finance.loanPrograms.yes') : $t('finance.loanPrograms.no') }}</span>
                                    <v-switch
                                        v-model="groupField.help"
                                        inset
                                        dense
                                        color="primary"
                                    ></v-switch>
                                </v-col>
                            </v-row>

                            <v-row v-if="groupField.help" >
                                <v-col cols="12">
                                    <label class="font-weight-medium mb-2 d-block">
                                        {{ $t('finance.loanPrograms.helpText') }}
                                    </label>
                                    <v-textarea
                                        v-model="groupField.helpText"
                                        outlined
                                        dense
                                        rows="3"
                                        :placeholder="$t('finance.loanPrograms.addHelpTextForThisDataField')"
                                    ></v-textarea>
                                </v-col>
                            </v-row>

                            <v-btn
                                v-if="fieldData.groupFields.length > 1"
                                icon
                                small
                                color="error"
                                @click="removeGroupField(index)"
                                
                            >
                                <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                        </div>

                        <v-btn
                            color="primary"
                            outlined
                            @click="addGroupField"
                            
                        >
                            {{ $t('finance.loanPrograms.addFieldToGroup') }}
                        </v-btn>
                    </template>

                    <!-- Help Toggle (for non-group fields or when group is disabled) -->
                    <v-row >
                        <v-col cols="6" class="d-flex align-center">
                            <label class="font-weight-medium mr-2 ">{{ $t('finance.loanPrograms.help') }}</label>
                            <v-tooltip top color="00BD73" max-width="350">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon size="medium" class="icon-with-background mr-2"
                                                style="color:#0EBF67" v-bind="attrs" v-on="on">
                                                mdi-alert-circle
                                            </v-icon>
                                        </template>
                                        <span>{{ $t('finance.loanPrograms.writeHelpTextUsers') }}</span>
                                    </v-tooltip>
                            <span class="mr-3">{{ fieldData.help ? $t('finance.loanPrograms.yes') : $t('finance.loanPrograms.no') }}</span>
                            <v-switch
                                v-model="fieldData.help"
                                inset
                                dense
                                color="primary"
                            ></v-switch>
                        </v-col>
                    </v-row>

                    <!-- Help Text (when Help is enabled) -->
                    <v-row v-if="fieldData.help">
                        <v-col cols="12">
                            <label class="font-weight-medium mb-2 d-block">
                                {{ $t('finance.loanPrograms.helpText') }}
                            </label>
                            <v-textarea
                                v-model="fieldData.helpText"
                                outlined
                                dense
                                rows="3"
                                :placeholder="$t('finance.loanPrograms.addHelpTextForThisDataField')"
                            ></v-textarea>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <!-- Footer Buttons -->
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn
                    outlined
                    color="primary"
                    @click="handleClose"
                    class="mr-3"
                    style="text-transform: uppercase; font-weight: bold;"
                >
                    {{ $t('finance.loanPrograms.cancelUpper') }}
                </v-btn>
                <v-btn
                    color="primary"
                    class="white--text font-weight-bold"
                    @click="handleSubmit"
                    style="text-transform: uppercase;"
                >
                    {{ $t('finance.loanPrograms.submitUpper') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { getAllowedFileTypesFromOptions } from '@/utils/loanProgramFieldPayload';

export default {
    name: 'AddDataField',
    props: {
        dialog: {
            type: Boolean,
            default: false,
        },
        editField: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            formValid: false,
            newOption: '',
            fieldData: {
                isMandetory: false,
                isGroup: false,
                groupTitle: '',
                dataType: null,
                fieldTitle: '',
                allowMultipleInputs: false,
                allowedFileTypes: [],
                options: [],
                dynamicDataList: null,
                unitType: '',
                measurementType: null,
                units: [],
                allowUserToChangeUnitType: false,
                digitalSignature: [],
                attachFile: [],
                allowedFormats: [],
                help: false,
                helpText: '',
                groupFields: [],
            },
            // Backend field types (must match finance_loan_program_data_fields migration ENUM)
            dataTypeOptions: [
                { label: this.$t('finance.loanPrograms.textField'), value: 'text_field' },
                { label: this.$t('finance.loanPrograms.textArea'), value: 'text_area' },
                { label: this.$t('finance.loanPrograms.textFieldNumeric'), value: 'text_field_numeric' },
                { label: this.$t('finance.loanPrograms.date'), value: 'date_field' },
                { label: this.$t('finance.loanPrograms.radioButton'), value: 'radio_button' },
                { label: this.$t('finance.loanPrograms.checkBoxes'), value: 'check_boxes' },
                { label: this.$t('finance.loanPrograms.dropdown'), value: 'dropdown' },
                { label: this.$t('finance.loanPrograms.measurementUnit'), value: 'measurement_unit' },
                { label: this.$t('finance.loanPrograms.attachFile'), value: 'attach_file' },
                { label: this.$t('finance.loanPrograms.digitalSignatureLabel'), value: 'digital_signature' },
            ],
            dynamicDataListOptions: [
                { label: this.$t('Leaf Size'), value: 'leaf-size' },
            ],
            measurementTypeOptions: [
                { label: this.$t('Cm'), value: 'cm' },
                { label: this.$t('M'), value: 'm' },
                { label: this.$t('Km'), value: 'km' },
                { label: this.$t('Inch'), value: 'inch' },
                { label: this.$t('Foot'), value: 'foot' },
            ],
        };
    },
    watch: {
        dialog(newVal) {
            if (newVal) {
                this.initializeFieldData();
            } else {
                this.newOption = '';
            }
        },
    },
    methods: {
        /** v-menu date picker in StepOne / forms expects _dateMenu on the field object. */
        ensureDateMenuState(fieldLike) {
            if (!fieldLike || fieldLike.dataType !== 'date_field') return;
            if (!Object.prototype.hasOwnProperty.call(fieldLike, '_dateMenu')) {
                this.$set(fieldLike, '_dateMenu', false);
            }
        },
        initializeFieldData() {
            if (this.editField) {
                // Deep clone to avoid reference issues
                this.fieldData = JSON.parse(JSON.stringify(this.editField));
                this.fieldData.isMandetory = !!(this.fieldData.isMandetory ?? this.fieldData.isMandetory ?? this.fieldData.isMandatory);
                this.ensureDateMenuState(this.fieldData);
                // Normalize choices options from API object format.
                if (this.fieldData.options && !Array.isArray(this.fieldData.options)) {
                    this.fieldData.options = Array.isArray(this.fieldData.options.choices) ? this.fieldData.options.choices : [];
                }
                if (this.fieldData.dataType === 'attach_file') {
                    const rawOpts = this.editField.options;
                    const optionsSource =
                        rawOpts && typeof rawOpts === 'object' && !Array.isArray(rawOpts) ? rawOpts : {};
                    let allowedFileTypes = Array.isArray(this.fieldData.allowedFileTypes)
                        ? [...this.fieldData.allowedFileTypes]
                        : [];
                    if (!allowedFileTypes.length) {
                        if (Array.isArray(optionsSource.allowedFileTypes)) {
                            allowedFileTypes = [...optionsSource.allowedFileTypes];
                        } else if (typeof optionsSource.allowedFileTypes === 'string') {
                            allowedFileTypes = [optionsSource.allowedFileTypes];
                        } else {
                            const formats = Array.isArray(optionsSource.allowedFormats) && optionsSource.allowedFormats.length
                                ? optionsSource.allowedFormats
                                : (Array.isArray(this.fieldData.allowedFormats) ? this.fieldData.allowedFormats : []);
                            allowedFileTypes = getAllowedFileTypesFromOptions({ ...optionsSource, allowedFormats: formats });
                        }
                    }
                    this.fieldData.allowedFileTypes = allowedFileTypes;
                    this.fieldData.allowedFormats = Array.isArray(optionsSource.allowedFormats) && optionsSource.allowedFormats.length
                        ? [...optionsSource.allowedFormats]
                        : (Array.isArray(this.fieldData.allowedFormats) ? [...this.fieldData.allowedFormats] : []);
                }
                // Normalize measurement_unit option payloads for edit UI.
                if (this.fieldData.dataType === 'measurement_unit') {
                    const rawMuOpts = this.editField.options;
                    const optionsSource =
                        rawMuOpts && typeof rawMuOpts === 'object' && !Array.isArray(rawMuOpts) ? rawMuOpts : {};
                    const units = Array.isArray(optionsSource.units) ? optionsSource.units : [];
                    this.fieldData.unitType = optionsSource.unitType || this.fieldData.unitType || null;
                    this.fieldData.units = units;
                    this.fieldData.measurementType = this.fieldData.measurementType || (units[0] || null);
                    this.fieldData.allowUserToChangeUnitType = !!(optionsSource.allowUserToChangeUnit ?? this.fieldData.allowUserToChangeUnitType);
                }
                // Normalize digital_signature option payloads for edit UI.
                if (this.fieldData.dataType === 'digital_signature') {
                    const rawSigOpts = this.editField.options;
                    const optionsSource =
                        rawSigOpts && typeof rawSigOpts === 'object' && !Array.isArray(rawSigOpts) ? rawSigOpts : {};
                    const ds = this.fieldData.digitalSignature;
                    const af = this.fieldData.attachFile;
                    this.fieldData.digitalSignature = (Array.isArray(ds) && ds.length > 0)
                        ? [...ds]
                        : (((typeof ds === 'boolean' && ds) || optionsSource.allowDigitalSignature) ? ['digital-signature'] : []);
                    this.fieldData.attachFile = (Array.isArray(af) && af.length > 0)
                        ? [...af]
                        : (((typeof af === 'boolean' && af) || optionsSource.allowFileUpload) ? ['attach-file'] : []);
                }
                // Ensure arrays are properly initialized
                if (!this.fieldData.options) this.fieldData.options = [];
                if (!this.fieldData.allowedFileTypes) this.fieldData.allowedFileTypes = [];
                if (!this.fieldData.groupFields) this.fieldData.groupFields = [];
                if (!this.fieldData.units) this.fieldData.units = [];
                if (!this.fieldData.digitalSignature) this.fieldData.digitalSignature = [];
                if (!this.fieldData.attachFile) this.fieldData.attachFile = [];
                if (!this.fieldData.allowedFormats) this.fieldData.allowedFormats = [];
                if (Array.isArray(this.fieldData.groupFields)) {
                    this.fieldData.groupFields = this.fieldData.groupFields.map(groupField => {
                        const normalizedGroupField = { ...groupField };
                        normalizedGroupField.isMandetory = !!(normalizedGroupField.isMandetory ?? normalizedGroupField.isMandetory ?? normalizedGroupField.isMandatory);
                        if (normalizedGroupField.options && !Array.isArray(normalizedGroupField.options)) {
                            normalizedGroupField.options = Array.isArray(normalizedGroupField.options.choices) ? normalizedGroupField.options.choices : [];
                        }
                        if (!Array.isArray(normalizedGroupField.options)) normalizedGroupField.options = [];

                        if (normalizedGroupField.dataType === 'attach_file') {
                            const rawGfOpts = groupField.options;
                            const optionsSource =
                                rawGfOpts && typeof rawGfOpts === 'object' && !Array.isArray(rawGfOpts) ? rawGfOpts : {};
                            let allowedFileTypes = Array.isArray(normalizedGroupField.allowedFileTypes)
                                ? [...normalizedGroupField.allowedFileTypes]
                                : [];
                            if (!allowedFileTypes.length) {
                                if (Array.isArray(optionsSource.allowedFileTypes)) {
                                    allowedFileTypes = [...optionsSource.allowedFileTypes];
                                } else if (typeof optionsSource.allowedFileTypes === 'string') {
                                    allowedFileTypes = [optionsSource.allowedFileTypes];
                                } else {
                                    const formats = Array.isArray(optionsSource.allowedFormats) && optionsSource.allowedFormats.length
                                        ? optionsSource.allowedFormats
                                        : (Array.isArray(normalizedGroupField.allowedFormats)
                                            ? normalizedGroupField.allowedFormats
                                            : []);
                                    allowedFileTypes = getAllowedFileTypesFromOptions({ ...optionsSource, allowedFormats: formats });
                                }
                            }
                            normalizedGroupField.allowedFileTypes = allowedFileTypes;
                            normalizedGroupField.allowedFormats = Array.isArray(optionsSource.allowedFormats) && optionsSource.allowedFormats.length
                                ? [...optionsSource.allowedFormats]
                                : (Array.isArray(normalizedGroupField.allowedFormats)
                                    ? [...normalizedGroupField.allowedFormats]
                                    : []);
                        }
                        if (normalizedGroupField.dataType === 'measurement_unit') {
                            const optionsSource = groupField.options && typeof groupField.options === 'object' && !Array.isArray(groupField.options)
                                ? groupField.options
                                : {};
                            const units = Array.isArray(normalizedGroupField.units) && normalizedGroupField.units.length
                                ? normalizedGroupField.units
                                : (Array.isArray(optionsSource.units) ? optionsSource.units : []);
                            normalizedGroupField.units = units;
                            normalizedGroupField.unitType = normalizedGroupField.unitType || optionsSource.unitType || null;
                            normalizedGroupField.measurementType = normalizedGroupField.measurementType || (units[0] || null);
                            normalizedGroupField.allowUserToChangeUnitType = !!(optionsSource.allowUserToChangeUnit ?? normalizedGroupField.allowUserToChangeUnitType);
                        }
                        if (normalizedGroupField.dataType === 'digital_signature') {
                            const optionsSource = groupField.options && typeof groupField.options === 'object' && !Array.isArray(groupField.options)
                                ? groupField.options
                                : {};
                            if (typeof normalizedGroupField.digitalSignature === 'boolean') {
                                normalizedGroupField.digitalSignature = normalizedGroupField.digitalSignature ? ['digital-signature'] : [];
                            }
                            if (typeof normalizedGroupField.attachFile === 'boolean') {
                                normalizedGroupField.attachFile = normalizedGroupField.attachFile ? ['attach-file'] : [];
                            }
                            if (!Array.isArray(normalizedGroupField.digitalSignature)) normalizedGroupField.digitalSignature = [];
                            if (!Array.isArray(normalizedGroupField.attachFile)) normalizedGroupField.attachFile = [];
                            const hasSelection = normalizedGroupField.digitalSignature.length > 0 || normalizedGroupField.attachFile.length > 0;
                            if (!hasSelection) {
                                normalizedGroupField.digitalSignature = optionsSource.allowDigitalSignature ? ['digital-signature'] : [];
                                normalizedGroupField.attachFile = optionsSource.allowFileUpload ? ['attach-file'] : [];
                            }
                        }

                        if (!Array.isArray(normalizedGroupField.allowedFileTypes)) normalizedGroupField.allowedFileTypes = [];
                        if (!Array.isArray(normalizedGroupField.allowedFormats)) normalizedGroupField.allowedFormats = [];
                        if (!Array.isArray(normalizedGroupField.units)) normalizedGroupField.units = [];
                        if (!Array.isArray(normalizedGroupField.digitalSignature)) normalizedGroupField.digitalSignature = [];
                        if (!Array.isArray(normalizedGroupField.attachFile)) normalizedGroupField.attachFile = [];
                        normalizedGroupField._newOption = normalizedGroupField._newOption || '';
                        normalizedGroupField.allowMultipleInputs = !!normalizedGroupField.allowMultipleInputs;
                        normalizedGroupField.allowUserToChangeUnitType = !!normalizedGroupField.allowUserToChangeUnitType;
                        this.ensureDateMenuState(normalizedGroupField);
                        return normalizedGroupField;
                    });
                }
            } else {
                this.fieldData = {
                    isMandetory: false,
                    isGroup: false,
                    groupTitle: '',
                    dataType: null,
                    fieldTitle: '',
                    allowMultipleInputs: false,
                    allowedFileTypes: [],
                    options: [],
                    dynamicDataList: null,
                    unitType: '',
                    measurementType: null,
                    units: [],
                    allowUserToChangeUnitType: false,
                    digitalSignature: [],
                    attachFile: [],
                    allowedFormats: [],
                    help: false,
                    helpText: '',
                    groupFields: [],
                };
            }
            this.newOption = '';
            // Reset form validation
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
        handleGroupToggle(value) {
            if (value && this.fieldData.groupFields.length === 0) {
                this.addGroupField();
            } else if (!value) {
                this.fieldData.groupFields = [];
                this.fieldData.groupTitle = '';
            }
        },
        addGroupField() {
            this.fieldData.groupFields.push({
                isMandetory: false,
                dataType: 'text_field',
                fieldTitle: '',
                help: false,
                helpText: '',
                options: [],
                _newOption: '',
                allowMultipleInputs: false,
                allowedFileTypes: [],
                allowedFormats: [],
                unitType: '',
                measurementType: null,
                units: [],
                allowUserToChangeUnitType: false,
                digitalSignature: [],
                attachFile: [],
            });
        },
        removeGroupField(index) {
            this.fieldData.groupFields.splice(index, 1);
        },
        handleGroupFieldDataTypeChange(index) {
            const gf = this.fieldData.groupFields[index];
            if (!gf) return;
            this.$set(gf, 'options', []);
            this.$set(gf, '_newOption', '');
            this.$set(gf, 'allowMultipleInputs', false);
            this.$set(gf, 'allowedFileTypes', []);
            this.$set(gf, 'allowedFormats', []);
            this.$set(gf, 'unitType', '');
            this.$set(gf, 'measurementType', null);
            this.$set(gf, 'units', []);
            this.$set(gf, 'allowUserToChangeUnitType', false);
            this.$set(gf, 'digitalSignature', []);
            this.$set(gf, 'attachFile', []);
        },
        addGroupFieldOption(index) {
            const groupField = this.fieldData.groupFields[index];
            if (groupField && groupField._newOption && groupField._newOption.trim()) {
                if (!groupField.options) {
                    this.$set(groupField, 'options', []);
                }
                groupField.options.push(groupField._newOption.trim());
                this.$set(groupField, '_newOption', '');
            }
        },
        removeGroupFieldOption(fieldIndex, optionIndex) {
            const groupField = this.fieldData.groupFields[fieldIndex];
            if (groupField && groupField.options) {
                groupField.options.splice(optionIndex, 1);
            }
        },
        handleDataTypeChange() {
            // Reset type-specific fields when data type changes
            this.fieldData.options = [];
            this.fieldData.allowedFileTypes = [];
            this.fieldData.allowMultipleInputs = false;
            this.fieldData.dynamicDataList = null;
            this.fieldData.unitType = null;
            this.fieldData.measurementType = null;
            this.fieldData.units = [];
            this.fieldData.allowUserToChangeUnitType = false;
            this.fieldData.digitalSignature = [];
            this.fieldData.attachFile = [];
            this.fieldData.allowedFormats = [];
        },
        addOption() {
            if (this.newOption.trim()) {
                if (!this.fieldData.options) {
                    this.fieldData.options = [];
                }
                this.fieldData.options.push(this.newOption.trim());
                this.newOption = '';
            }
        },
        removeOption(index) {
            this.fieldData.options.splice(index, 1);
        },
        handleClose() {
            this.$emit('close');
            this.initializeFieldData();
        },
        handleSubmit() {
            if (this.$refs.form.validate()) {
                // Validate group fields if group is enabled
                const optionRequiredTypes = ['dropdown', 'radio_button', 'check_boxes'];
                
                if (this.fieldData.isGroup) {
                    if (!this.fieldData.groupTitle) {
                        this.$notify({
                            type: 'error',
                            text: this.$t('finance.loanPrograms.groupTitleRequired'),
                        });
                        return;
                    }
                    if (this.fieldData.groupFields.length === 0) {
                        this.$notify({
                            type: 'error',
                            text: this.$t('finance.loanPrograms.atLeastOneFieldRequiredInGroup'),
                        });
                        return;
                    }
                    
                    // Validate each group field
                    for (let i = 0; i < this.fieldData.groupFields.length; i++) {
                        const groupField = this.fieldData.groupFields[i];
                        if (!groupField.fieldTitle) {
                            this.$notify({
                                type: 'error',
                                text: this.$t('finance.loanPrograms.fieldTitleRequiredForAllGroupFields'),
                            });
                            return;
                        }
                        if (optionRequiredTypes.includes(groupField.dataType) && (!groupField.options || groupField.options.length === 0)) {
                            this.$notify({
                                type: 'error',
                                text: this.$t('finance.loanPrograms.atLeastOneOptionRequiredFor') + ' "' + groupField.fieldTitle + '"',
                            });
                            return;
                        }
                        if (groupField.dataType === 'attach_file' && (!groupField.allowedFileTypes || groupField.allowedFileTypes.length === 0)) {
                            this.$notify({
                                type: 'error',
                                text: this.$t('finance.loanPrograms.atLeastOneFileTypeMustBeSelected'),
                            });
                            return;
                        }
                    }
                } else {
                    if (!this.fieldData.fieldTitle) {
                        this.$notify({
                            type: 'error',
                            text: this.$t('finance.loanPrograms.fieldTitleRequired'),
                        });
                        return;
                    }
                }

                if (!this.fieldData.isGroup && optionRequiredTypes.includes(this.fieldData.dataType) && (!this.fieldData.options || this.fieldData.options.length === 0)) {
                    this.$notify({
                        type: 'error',
                        text: this.$t('finance.loanPrograms.atLeastOneOptionRequired'),
                    });
                    return;
                }

                if (this.fieldData.dataType === 'attach_file' && (!this.fieldData.allowedFileTypes || this.fieldData.allowedFileTypes.length === 0)) {
                    this.$notify({
                        type: 'error',
                        text: this.$t('finance.loanPrograms.atLeastOneFileTypeMustBeSelected'),
                    });
                    return;
                }

                // Clean up temporary _newOption fields before submitting
                if (this.fieldData.isGroup && this.fieldData.groupFields) {
                    this.fieldData.groupFields.forEach(gf => {
                        delete gf._newOption;
                    });
                }

                this.$emit('submit', { ...this.fieldData });
                this.handleClose();
            }
        },
    },
};
</script>

<style scoped>
label {
    font-size: 14px;
    color: #333;
}
</style>
